/**
 * Правила обработки входящих сигналов звонка (стори 17.1 / QA-1.1).
 *
 * <p><b>Зачем отдельный модуль.</b> Решения «принять сигнал или молча
 * выбросить» жили внутри подписки в `useVoiceCallSession` — россыпью вложенных
 * `if (...) return;` на несколько сотен строк, поверх рефов и эффектов. Проверить
 * их можно было только вживую, вдвоём с телефонами, и почти все известные
 * дефекты звонков («accept ignored: group requires callInviteId», двойной
 * accept, чужая нога, параллельный invite) — это ошибки ровно здесь.
 *
 * <p>Правила чистые: на вход — снимок состояния и разобранный сигнал, на выход
 * — решение. Никаких рефов, таймеров и сети, поэтому их можно перебрать
 * тестами целиком, включая комбинации, которые руками не воспроизвести.
 *
 * <p><b>Что здесь НЕ живёт.</b> Эффекты: разбор SDP, teardown, отправка
 * ответных сигналов. Модуль отвечает на вопрос «действовать ли и почему»,
 * а не «как».
 */

export type CallScope = 'direct' | 'group';
export type CallPhase = 'idle' | 'calling' | 'ringing' | 'incall';

/**
 * Нормализация id пользователя перед сравнением.
 *
 * <p>Дефисы убираются намеренно: id — это UUID, и разные источники присылают
 * его то в каноническом виде, то без дефисов (WS-конверт, payload инвайта,
 * данные пуша). Сравнение «как есть» считало бы одного и того же человека
 * разными людьми и отбрасывало его сигналы как чужие.
 */
export function normCallUserId(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim().replace(/-/g, '').toLowerCase() : '';
}

/**
 * Совпадает ли отправитель с тем, от кого мы ждём сигналы.
 *
 * <p>Пустой «ожидаемый» означает, что собеседник ещё не известен, — тогда
 * возражать не на чем и сигнал не отбрасывается. Это не послабление: до первого
 * accept/invite мы действительно не знаем, кто на том конце.
 */
export function isUnexpectedPeer(
  expectedPeerUserId: string | null | undefined,
  fromUserId: string | null | undefined,
): boolean {
  const expected = normCallUserId(expectedPeerUserId);
  const from = normCallUserId(fromUserId);
  if (!expected || !from) return false;
  return expected !== from;
}

/**
 * Не от чужой ли «ноги» звонка пришёл сигнал.
 *
 * <p>`requireLeg` разводит два режима, которые в исходном коде выглядели
 * одинаково, а вели себя по-разному:
 *
 * <ul>
 *   <li>{@code false} — нога проверяется, только если она в сигнале есть.
 *       Так обрабатываются `decline` и `mic_state`: старые клиенты ногу не
 *       присылают, и терять из-за этого отбой нельзя;</li>
 *   <li>{@code true} — сигнал без ноги тоже чужой. Так обрабатываются SDP и ICE
 *       в группе: там одновременно живут несколько разговоров, и кадр без ноги
 *       невозможно отнести к нужному.</li>
 * </ul>
 *
 * <p>Пока своя нога не привязана (`boundCallInviteId` пуст), не отбрасываем
 * ничего: привязка происходит как раз из первого такого сигнала.
 */
export function isForeignCallLeg(
  boundCallInviteId: string | null | undefined,
  legFromMessage: string | null | undefined,
  opts: { requireLeg: boolean },
): boolean {
  const bound = boundCallInviteId ?? '';
  if (!bound) return false;
  const leg = legFromMessage ?? '';
  if (!leg) return opts.requireLeg;
  return leg !== bound;
}

/** Решение по входящему `accept` (мы звонящий, нам ответили). */
type AcceptDecision =
  /** Поднимать свою сторону: отправлять offer / заходить в комнату. */
  | { action: 'start' }
  /** Ещё один участник группы вошёл в ту же комнату SFU — это норма. */
  | { action: 'ignore'; reason: 'group_extra_participant' }
  /** Линия занята: на прямой звонок ответили дважды. */
  | { action: 'busy' }
  | { action: 'ignore'; reason: 'missing_leg_in_group' | 'foreign_leg' | 'not_calling' };

/**
 * Что делать с `accept`.
 *
 * <p>Порядок проверок повторяет исходный и важен: сначала нога (чужой разговор
 * не должен вообще влиять на наш), потом фаза, и только потом дубль.
 */
export function decideAccept(state: {
  callScope: CallScope;
  phase: CallPhase;
  boundCallInviteId: string | null;
  acceptLeg: string | null;
  /** Мы уже начали поднимать свою сторону по предыдущему accept. */
  callerOfferStarted: boolean;
}): AcceptDecision {
  if (state.boundCallInviteId) {
    if (!state.acceptLeg && state.callScope === 'group') {
      // Без ноги в группе не понять, к какому из разговоров относится ответ.
      return { action: 'ignore', reason: 'missing_leg_in_group' };
    }
    if (state.acceptLeg && state.acceptLeg !== state.boundCallInviteId) {
      return { action: 'ignore', reason: 'foreign_leg' };
    }
  }
  if (state.phase !== 'calling') {
    return { action: 'ignore', reason: 'not_calling' };
  }
  if (state.callerOfferStarted) {
    if (state.callScope === 'group') {
      /*
       * В группе разговор идёт через SFU, и каждый участник заходит в комнату
       * сам — второй и третий accept здесь норма, а не занятая линия. Раньше
       * на них уходил `call_line_busy`, и тот, кто ответил вторым, вылетал из
       * звонка, куда имел полное право войти.
       */
      return { action: 'ignore', reason: 'group_extra_participant' };
    }
    return { action: 'busy' };
  }
  return { action: 'start' };
}

/**
 * Пришёл ли кадр от участника группы, на котором наш звонок <b>не</b> держится.
 *
 * <p>«Наш» — это звонящий, пока нам звонят, и ответивший, пока идёт разговор. Все остальные в
 * группе — такие же приглашённые: их отбой и отказ к нашему разговору отношения не имеют. В прямом
 * звонке таких третьих нет по определению, поэтому там всегда {@code false}.
 *
 * <p>Неизвестный отправитель или неизвестный собеседник — не повод считать кадр чужим: пока мы не
 * знаем состава, поведение остаётся прежним (см. {@link isUnexpectedPeer}).
 */
function isFromAnotherGroupMember(state: {
  callScope: CallScope;
  counterpartUserId: string | null;
  fromUserId: string | null;
}): boolean {
  return state.callScope === 'group' && isUnexpectedPeer(state.counterpartUserId, state.fromUserId);
}

/**
 * Решение по входящему `decline`.
 *
 * @param counterpartUserId тот, на ком держится наш звонок: для принимающего — звонящий, для
 *     звонящего — тот, кто ответил. Пусто, пока он неизвестен.
 */
export function decideDecline(state: {
  /** Мы принимающая сторона. */
  incoming: boolean;
  callScope: CallScope;
  phase: CallPhase;
  boundCallInviteId: string | null;
  legFromMessage: string | null;
  fromUserId: string | null;
  counterpartUserId: string | null;
}): { action: 'teardown'; postDeclinedOutgoing: boolean } | { action: 'ignore'; reason: string } {
  if (isForeignCallLeg(state.boundCallInviteId, state.legFromMessage, { requireLeg: false })) {
    return { action: 'ignore', reason: 'foreign_leg' };
  }
  if (isFromAnotherGroupMember(state)) {
    // Отказался такой же приглашённый. Раньше его отказ снимал экран входящего у всех остальных:
    // проверка ниже защищала только звонящего, а всех прочих — никто.
    return { action: 'ignore', reason: 'group_other_member' };
  }
  if (!state.incoming && state.callScope === 'group' && state.phase === 'calling') {
    // Один отказался — остальные ещё звонят. Групповой вызов не отменяется
    // первым же «занят».
    return { action: 'ignore', reason: 'group_ring_continues' };
  }
  return {
    action: 'teardown',
    // «Отклонён» пишем в переписку только звонящему: принимающему это его же
    // собственный отбой.
    postDeclinedOutgoing: !state.incoming && state.phase === 'calling',
  };
}

/** Решение по входящему `bye`. */
type ByeDecision =
  | { action: 'teardown'; postMissedIncoming: boolean; playEndedTone: boolean }
  | {
      action: 'ignore';
      reason: 'group_other_member' | 'group_ring_continues' | 'group_participant_left';
    };

/**
 * Что делать с `bye`.
 *
 * <p><b>В прямом звонке — ничего не фильтруем.</b> Сторон две, разговор кончился в любом случае;
 * вопрос только в том, что показать и что проиграть.
 *
 * <p><b>В групповом — фильтруем, и это исправление дефекта.</b> Отказ у принимающего — это не один
 * кадр, а два: `decline` и следом `bye` из общего сброса сессии. Отказ групповой вызов не отменял
 * ({@link decideDecline} и её {@code group_ring_continues}), а `bye` шёл дальше без единой
 * проверки — и первый же отказавшийся клал трубку всем, кому ещё звонили. То есть защита
 * группового вызова не работала вовсе: она стояла на одном кадре из двух.
 *
 * <p>Правило здесь одно: в группе `bye` — это «я ушёл», а не «разговор окончен». Свой звонок мы
 * заканчиваем только тогда, когда после этого ухода нам не с кем говорить:
 *
 * <ul>
 *   <li>нам звонят, и ушёл <b>не звонящий</b>, а другой приглашённый — нас это не касается;</li>
 *   <li>звоним мы, и отказался один из приглашённых — остальным продолжаем звонить (если не
 *       ответит никто, звонок закроет обычный таймаут ожидания);</li>
 *   <li>идёт разговор, и в комнате остались другие — остаёмся с ними.</li>
 * </ul>
 *
 * <p>Везде, где состав <b>неизвестен</b> ({@code counterpartUserId} или {@code fromUserId} пусты,
 * комнаты SFU нет), решение прежнее — завершать. Ошибка в эту сторону дешевле: лишний отбой человек
 * видит сразу и перезвонит, а зависший экран звонка в пустой комнате не лечится ничем, кроме
 * перезапуска приложения.
 *
 * @param counterpartUserId тот, на ком держится наш звонок: для принимающего — звонящий, для
 *     звонящего — тот, кто ответил. Пусто, пока он неизвестен.
 * @param othersLeftInRoom сколько других участников остаётся в комнате SFU <b>после</b> этого
 *     ухода, или {@code null}, если комнаты нет и состав неизвестен
 */
export function decideBye(state: {
  incoming: boolean;
  phase: CallPhase;
  callScope: CallScope;
  fromUserId: string | null;
  counterpartUserId: string | null;
  othersLeftInRoom: number | null;
}): ByeDecision {
  const teardown = {
    action: 'teardown' as const,
    // Положили трубку, пока у нас ещё звонило, — это пропущенный.
    postMissedIncoming: state.incoming && state.phase === 'ringing',
    playEndedTone: state.phase === 'incall',
  };
  if (state.callScope !== 'group') {
    return teardown;
  }
  if (state.phase === 'calling') {
    // Групповой вызов не отменяется первым же отказом — ровно то же правило, что и у `decline`.
    // Если не ответит никто, звонок закроет обычный таймаут ожидания.
    return { action: 'ignore', reason: 'group_ring_continues' };
  }
  if (state.phase === 'incall' && state.othersLeftInRoom !== null) {
    // Состав комнаты известен точно — он и решает, есть ли ещё с кем говорить.
    return state.othersLeftInRoom > 0
      ? { action: 'ignore', reason: 'group_participant_left' }
      : teardown;
  }
  return isFromAnotherGroupMember(state)
    ? { action: 'ignore', reason: 'group_other_member' }
    : teardown;
}

/**
 * Относится ли `call_line_busy` к нам.
 *
 * <p>Адресата проверяем самим: сигнал веерный, и без этого «линия занята»
 * роняла звонок у всех, кто оказался в чате.
 *
 * <p><b>Направление сигнала намеренно не проверяется.</b> Раньше здесь стояло
 * {@code if (!state.incoming) return false;} — то есть «занято» принимал только
 * принимающий. Это отражало единственный случай, который тогда существовал:
 * звонящий отвечает «занято» второму ответившему в прямом чате. Но занятой
 * бывает и обратная сторона — собеседник уже разговаривает, — и тогда «занято»
 * летит именно <b>звонящему</b>, у которого {@code incoming === false}. С
 * прежней проверкой он этот ответ выбрасывал и продолжал слушать гудки в
 * пустоту. Адресность держит проверка {@code targetUserId === self}: свой же
 * веерный отзвук её не проходит.
 */
export function isCallLineBusyForUs(state: {
  phase: CallPhase;
  selfUserId: string | null;
  targetUserId: string | null;
  busyLeg: string | null;
  boundCallInviteId: string | null;
}): boolean {
  if (state.phase === 'idle') return false;
  const target = normCallUserId(state.targetUserId);
  const self = normCallUserId(state.selfUserId);
  if (!target || !self || target !== self) return false;
  // Нога не обязательна (старые клиенты её не шлют), но если обе стороны её
  // знают — должна совпасть.
  if (state.busyLeg && state.boundCallInviteId && state.busyLeg !== state.boundCallInviteId) {
    return false;
  }
  return true;
}

/**
 * Заменяет ли SFU этот сигнал. В группе поверх LiveKit свои SDP и ICE не нужны:
 * их роль выполняет сервер, а mesh-кадры от других участников только путают
 * соединение.
 */
export function isReplacedByGroupSfu(state: {
  callScope: CallScope;
  hasLiveKitConnection: boolean;
  signalType: string;
}): boolean {
  return (
    state.callScope === 'group' &&
    state.hasLiveKitConnection &&
    (state.signalType === 'offer' || state.signalType === 'answer' || state.signalType === 'ice')
  );
}

/**
 * Общая проверка для SDP и ICE в группе: и чужой участник, и чужая нога.
 * Вынесена, потому что в исходнике повторялась четырьмя почти одинаковыми
 * блоками — у `offer` в разговоре, `offer` в очереди, `answer` и `ice`.
 */
export function isForeignGroupMediaSignal(state: {
  callScope: CallScope;
  expectedPeerUserId: string | null;
  fromUserId: string | null;
  boundCallInviteId: string | null;
  legFromMessage: string | null;
}): boolean {
  if (isUnexpectedPeer(state.expectedPeerUserId, state.fromUserId)) return true;
  if (state.callScope !== 'group') return false;
  return isForeignCallLeg(state.boundCallInviteId, state.legFromMessage, { requireLeg: true });
}

/*
 * `isParallelInvite` жил здесь и молча выбрасывал второе приглашение, пока
 * звонит первое. Его заменил `decideIncomingInviteWhileBusy` из
 * `callBusyRules`: молчание было половиной решения — ногу первого звонка оно
 * действительно защищало, но второй звонящий при этом слушал гудки до упора и
 * не узнавал, что линия занята. И покрывало оно только фазу `ringing`, оставляя
 * без ответа и звонок посреди разговора, и встречный вызов.
 */

/** Что делать, когда вышло время ожидания ответа. */
export type CallWaitTimeoutDecision =
  /** Фаза уже не та — таймер пережил своё время, ничего не делаем. */
  | { kind: 'ignore' }
  /** Ответ уже идёт — рвать звонок нельзя. */
  | { kind: 'keep' }
  /** Группа, звонящий: в комнате кто-то есть — значит, ответили. */
  | { kind: 'answered' }
  /** Не дождались. `postMissed` — писать ли итог звонка в чат. */
  | { kind: 'give_up'; side: 'outgoing' | 'incoming'; postMissed: boolean };

/**
 * Решение по тайм-ауту ожидания ответа (45 с).
 *
 * <p><b>Три поломки группового звонка, которые это закрывает</b> (прогон на эмуляторах
 * 2026-09-27):
 * <ul>
 *   <li>`accept` принимающий шлёт только после того, как поднял медиа: вход в SFU, публикация
 *       камеры, повтор при сбое согласования. На медленной сети это дольше ожидания, и звонящий
 *       уходил с «Нет ответа», хотя в комнате уже сидели ответившие;</li>
 *   <li>тот же тайм-аут у принимающего срабатывал посреди его собственного ответа — отправлял
 *       decline и bye, и ответивший вылетал из звонка;</li>
 *   <li>«пропущенный» от каждого принимающего падал в общий чат группы: на один звонок — по
 *       сообщению от всех, кто не ответил. Итог группового звонка пишет звонящий.</li>
 * </ul>
 */
export function decideCallWaitTimeout(state: {
  phase: CallPhase;
  incoming: boolean;
  callScope: CallScope;
  acceptInFlight: boolean;
  /** Сколько других участников сейчас в комнате SFU (0, если комнаты нет). */
  remoteParticipantsInRoom: number;
}): CallWaitTimeoutDecision {
  if (state.phase === 'calling' && !state.incoming) {
    if (state.callScope === 'group' && state.remoteParticipantsInRoom > 0) {
      return { kind: 'answered' };
    }
    return { kind: 'give_up', side: 'outgoing', postMissed: true };
  }
  if (state.phase === 'ringing' && state.incoming) {
    if (state.acceptInFlight) return { kind: 'keep' };
    return { kind: 'give_up', side: 'incoming', postMissed: state.callScope !== 'group' };
  }
  return { kind: 'ignore' };
}
